// Pure helper functions for portal wizard flow logic — no React, no side effects.

export const SECTION_CODES = [
  "contactgegevens",
  "groepen",
  // School-wide closure days. The per-group vrije dagdelen + vakdocenten now live inside the
  // group dialog (Groepen step), so they are no longer separate stepper sections.
  "studiedagen",
  "controleren",
] as const;

export const PROGRAMMA_SECTION_CODES = [
  "vast_keuze",
  "technohub",
  "deskundigheidsbevordering",
  "maatwerk",
  "programma_controleren",
] as const;

export type ProgrammaSectionCode = (typeof PROGRAMMA_SECTION_CODES)[number];

export type SectionCode = (typeof SECTION_CODES)[number];

export type SectionStatus =
  | "not_open"
  | "locked"
  | "todo"
  | "in_progress"
  | "done";

/** Whether a section's content matches the last aanlevering of its flow. Drives the
 *  stepper icon (green = delivered & unchanged, amber = changed), independent of the
 *  completeness that `status` tracks. */
export type DeliveryState = "no_version" | "delivered" | "changed";

export type SectionMeta = {
  code: SectionCode;
  title: string;
  summary: string;
  status: SectionStatus;
  deliveryState?: DeliveryState;
  deadline?: string;
  /** Beschikbaarheid sub-sections: the school answered "ik vul dit later in". The flow
   *  status can still be "done" (confirmed, so it never blocks), but the stepper shows a
   *  clock instead of a green check so a deferred answer never reads as fully complete. */
  pendingLater?: boolean;
};

export function isSectionCode(value: string | null | undefined): value is SectionCode {
  return !!value && (SECTION_CODES as readonly string[]).includes(value);
}

export function isCompleted(status: SectionStatus): boolean {
  return status === "done";
}

export function isUnavailable(status: SectionStatus): boolean {
  return status === "not_open" || status === "locked";
}

/** Can a section be opened? Either all predecessors are completed, or
 *  this section already has data (user previously reached it). This prevents
 *  locking out sections the user already worked on when a predecessor's
 *  submittedAt is invalidated due to data changes. */
export function canOpenSection(sections: SectionMeta[], code: SectionCode): boolean {
  const index = sections.findIndex((s) => s.code === code);
  if (index === -1) return false;
  const section = sections[index]!;
  // Schools navigate freely between sections; the only hard gate is the final aanleveren
  // (which needs every section complete). A section is only un-openable if its window has
  // not opened yet. Previously an incomplete section blocked all later ones and the
  // canonical redirect teleported you back, which is wrong now that the buttons are pure
  // navigation.
  return !isUnavailable(section.status);
}

/** First section that's not completed and not unavailable. */
export function getNextRequired(sections: SectionMeta[]): SectionCode | null {
  for (const s of sections) {
    if (isUnavailable(s.status)) return null;
    if (!isCompleted(s.status)) return s.code;
  }
  // All done → go to last (controleren)
  return sections.at(-1)?.code ?? null;
}

/** Given a requested section from the URL, return the canonical (valid) section to show. */
export function getCanonicalSection(
  sections: SectionMeta[],
  requested: SectionCode | null,
): SectionCode | null {
  if (sections.length === 0) return null;
  if (requested && canOpenSection(sections, requested)) return requested;
  const next = getNextRequired(sections);
  if (next) return next;
  // Never blank the page: if the next-required is blocked (e.g. an early section
  // is locked after its window closed), fall back to the first section the user
  // can still open or has already completed so they can at least review it.
  const viewable = sections.find((s) => canOpenSection(sections, s.code) || isCompleted(s.status));
  return viewable?.code ?? sections.at(-1)?.code ?? null;
}

export function getCompletedCount(sections: SectionMeta[]): number {
  return sections.filter((s) => isCompleted(s.status)).length;
}

export function sectionToApiCode(code: SectionCode | ProgrammaSectionCode): string {
  switch (code) {
    case "contactgegevens": return "school_info";
    case "groepen": return "class_config";
    case "studiedagen": return "closure_days";
    case "controleren": return "review";
    case "vast_keuze": return "optional_programme";
    case "technohub": return "technohub_programme";
    case "deskundigheidsbevordering": return "deskundigheidsbevordering_programme";
    case "maatwerk": return "maatwerk_programme";
    case "programma_controleren": return "programme_review";
  }
}

export function isProgrammaSectionCode(value: string | null | undefined): value is ProgrammaSectionCode {
  return !!value && (PROGRAMMA_SECTION_CODES as readonly string[]).includes(value);
}

/** Gegevens sections: "done" means the section's content is COMPLETE (valid + answered),
 *  NOT that a per-section confirm was pressed (there is no per-section submit anymore).
 *  Completeness gates navigation + the Controleren aanleveren button; the green/amber icon
 *  is driven separately by `deliveryState`. Only the opening window can still lock a section. */
export function deriveGegevensSectionStatus(step: any, complete: boolean, hasData: boolean): SectionStatus {
  const now = new Date();
  const opensAt = step?.opensAt ? new Date(step.opensAt) : null;
  if (opensAt && opensAt > now) return "not_open";
  if (complete) return "done";
  if (hasData) return "in_progress";
  return "todo";
}

export function deriveSectionStatus(step: any, hasData: boolean): SectionStatus {
  if (!step) return hasData ? "in_progress" : "todo";

  const now = new Date();
  const opensAt = step.opensAt ? new Date(step.opensAt) : null;
  const submittedAt = step.submittedAt ? new Date(step.submittedAt) : null;

  // Only the opening time gates a section; schools edit + resubmit freely afterwards
  // (matches the server's requireSectionOpen), so a closed window never locks the UI.
  // Editing a submitted section clears its submittedAt → it becomes "in_progress"
  // again (re-confirm needed), and stays navigable.
  if (opensAt && opensAt > now) return "not_open";
  if (submittedAt) return "done";
  if (step.draftSavedAt) return "in_progress";
  if (hasData) return "in_progress";

  return "todo";
}
